import { useEffect, useRef } from 'react';
import { navigate, routePath, type Route } from '../platform/router.js';
import { t, type TranslationKey } from '../platform/i18n.js';
import { useLanguage } from '../platform/use-language.js';
import { useNavSheet, setNavSheetOpen } from '../platform/nav-sheet.js';
import { useNavCounts, refreshNavCounts } from '../platform/nav-counts-store.js';
import { lockBodyScroll } from '../platform/scroll-lock.js';
import type { NavCounts } from '../../../api/src/catalog/nav-counts.js';

/**
 * The persistent navigation (redesign phase 1) — before this, navigation was a wall
 * of twelve buttons at the top of the Items screen and nowhere else.
 *
 * SHAPE: one <nav>, rendered once in the shell. CSS decides the layout — a bottom
 * tab bar on phones (primary destinations + a More group), a full sidebar on
 * desktop (everything visible, More hidden). The DOM is the SAME in both layouts:
 * every destination exists exactly once, because Playwright's strict mode counts
 * hidden nodes too and ~20 e2e call sites click these buttons by name.
 *
 * BUTTONS, NOT LINKS, deliberately: the whole app navigates through `navigate()`
 * (see router.ts), every existing screen exposed its navigation as buttons, and the
 * e2e suite addresses them as buttons. Consistency wins over anchor purism here.
 */

export type Destination =
  | 'boxes'
  | 'retour'
  | 'dashboard'
  | 'items'
  | 'scan'
  | 'reservations'
  | 'checkouts'
  | 'maintenance'
  | 'outfits'
  | 'projects'
  | 'racks'
  | 'settings';

/**
 * THE BAR FOLLOWS THE GOODS (maintainer, 5 Aug 2026): boxes, items, outfits, reservations,
 * check-outs — a garment arrives in a box, is catalogued as an item, is composed into an
 * outfit, is promised on a reservation, and leaves at a check-out. That is the order of the
 * work, and it is now the order of the tabs.
 *
 * ⚠️ WHAT MOVED OUT, AND WHY IT IS WORTH SAYING: `dashboard` and `scan` were primary and are
 * now behind More. Boxes-first survives from the 4 Aug ruling ("inbound is where a working day
 * starts") — the rest of that arrangement was arranged around reading rather than around the
 * path a garment takes.
 *
 * `scan` losing its slot is the one to watch: scanning IS a field act, and this puts it a tap
 * further away on a phone. Said out loud because if it turns out to be wrong, this comment is
 * where the reason it was accepted should be argued with.
 */
const PRIMARY: Destination[] = ['boxes', 'items', 'outfits', 'reservations', 'checkouts'];

/**
 * WHO SEES WHICH AREA — maintainer, 10 Aug 2026: *"a user who doesn't have access to certain
 * areas, shouldn't see those areas."*
 *
 * ⚠️ THIS HIDES, IT DOES NOT GATE — the standing rule is unchanged: client-side roles render,
 * the SERVER refuses. A hidden tab is a kindness (no doorway into a screen whose every button
 * would refuse you); the deep link still resolves, and RLS still decides what any request
 * returns. If this list ever disagrees with the 005 matrix, the matrix wins and the only
 * damage is a tab that errors — never a grant.
 *
 * The judgement per role is "where does their WORK live", derived from the matrix's write
 * capabilities, not from what they may read (read is everyone — a nav built on read would
 * hide nothing). Recorded per role so a wrong call is arguable line by line:
 *  - `student` works the goods out and in: no boxes (inbound is operate), no retour.
 *  - `data_entry` catalogues: items and little else.
 *  - `catalog_admin` is data_entry plus the catalogue's shape; boxes stay visible because
 *    intake starts from a box (`/add?box=…`).
 *  - `self_service` browses and books for themselves: items, outfits, their reservations.
 *  - `view_only` reads everything, so sees everything except `scan` — a tool for acting.
 */
const VISIBLE: Record<string, readonly Destination[]> = {
  admin: ['boxes', 'retour', 'dashboard', 'items', 'scan', 'reservations', 'checkouts', 'maintenance', 'outfits', 'projects', 'racks', 'settings'],
  staff: ['boxes', 'retour', 'dashboard', 'items', 'scan', 'reservations', 'checkouts', 'maintenance', 'outfits', 'projects', 'racks', 'settings'],
  student: ['dashboard', 'items', 'scan', 'reservations', 'checkouts', 'maintenance', 'outfits', 'racks', 'settings'],
  data_entry: ['dashboard', 'items', 'racks', 'settings'],
  catalog_admin: ['dashboard', 'items', 'outfits', 'boxes', 'racks', 'settings'],
  self_service: ['items', 'outfits', 'reservations', 'settings'],
  view_only: ['boxes', 'retour', 'dashboard', 'items', 'reservations', 'checkouts', 'maintenance', 'outfits', 'projects', 'racks', 'settings'],
};

/**
 * `null` (whoami not answered yet, or failed) shows EVERYTHING. The alternative — an empty
 * nav that fills in a beat later — punishes every user on every load to hide tabs from the
 * rare unknown; and an unknown role pressing a tab it should not see meets the server's
 * refusal, which is the real guarantee anyway.
 */
export function visibleDestinations(role: string | null): readonly Destination[] {
  return (role !== null ? VISIBLE[role] : undefined) ?? (Object.keys(LABEL) as Destination[]);
}
/** At the desk: everything else, one tap further on a phone. */
const SECONDARY: Destination[] = [
  /**
   * ⚠️ SECONDARY, NOT PRIMARY, AND THAT IS A JUDGEMENT TO ARGUE WITH.
   *
   * "The bar follows the goods" (5 Aug) put five destinations in the primary bar and retour is
   * genuinely the sixth leg of that path. But a sixth slot makes every one of them narrower on
   * a phone, and stage 2 is READ-ONLY — nobody works from it yet, they look at it. If the
   * atelier starts opening this daily it has earned a slot, and taking one from `checkouts`
   * (which is where a retour ends up anyway) is the trade to make.
   */
  'retour',
  'projects',
  'racks',
  'scan',
  'dashboard',
  'maintenance',
  'settings',
];

export const LABEL: Record<Destination, TranslationKey> = {
  boxes: 'nav.boxes',
  retour: 'nav.retour',
  dashboard: 'nav.dashboard',
  items: 'nav.itemsTab',
  scan: 'nav.scan',
  reservations: 'nav.reservations',
  checkouts: 'nav.checkouts',
  maintenance: 'nav.maintenance',
  outfits: 'nav.outfits',
  projects: 'nav.projects',
  racks: 'nav.racks',
  settings: 'nav.settings',
};

/** Which tab a route lights up — detail and intake screens belong to their list. */
export function activeTab(route: Route): Destination {
  if (route.name === 'item') return 'items';
  // Adding INTO a box belongs to the boxes tab; a plain add belongs to items.
  if (route.name === 'add') return route.boxId !== undefined ? 'boxes' : 'items';
  if (route.name === 'box' || route.name === 'boxAdd') return 'boxes';
  if (route.name === 'outfit') return 'outfits';
  // A project or character detail belongs to the Projects tab (063).
  if (route.name === 'projects' || route.name === 'project' || route.name === 'character') return 'projects';
  // A booking DETAIL belongs to whichever tab lists it: draft and confirmed are
  /**
   * ⚠️ IT LIGHTS THE TAB YOU CAME FROM — 0.1.181, reported from use: *"it moves to the
   * reservations tab, i see the highlight change in the main nav from check out to
   * reservations"*.
   *
   * `/bookings/:id` serves both tabs. This used to answer `reservations` unconditionally,
   * reasoning that the nav does not know a booking's status and Reservations is where a
   * booking is created and spends most of its life. That reasoning was about STATUS, and
   * the question is not status — it is which list the person is working through. The route
   * now carries that (`?from=checkouts`), so the nav no longer has to guess.
   *
   * A booking opened from anywhere else — the calendar on Reservations, a dashboard link, a
   * pasted URL — still lights Reservations, which is the honest default when nothing says
   * otherwise.
   */
  if (route.name === 'booking') return route.from === 'checkouts' ? 'checkouts' : 'reservations';
  // The per-garment check-in (frame 4d) is a detail screen — `.app-detail` hides this nav —
  // so this is answered only to keep the union exhaustive. It comes off the Check-outs board,
  // so Check-outs is the honest tab if the value is ever used.
  if (route.name === 'booking-checkin') return 'checkouts';
  // The client view has no tab and never renders this nav — it is answered here only so
  // the union stays exhaustive, which is what made the compiler point at this line the
  // moment the route was added.
  // `users` and `import` are Settings panels now; their routes light the Settings tab.
  if (route.name === 'users' || route.name === 'import') return 'settings';
  /**
   * ⚠️ `join` HAS NO TAB AND NEVER RENDERS THIS NAV — `app.tsx` returns before the nav exists,
   * because the person reading it has no seat. It is answered here only to keep the union
   * exhaustive, exactly as the client view was, and the compiler pointing at this line is the
   * mechanism working rather than a nuisance. `dashboard` is an arbitrary safe answer: if this
   * value is ever USED, the bug is that the nav rendered at all.
   */
  if (route.name === 'join') return 'dashboard';
  return route.name;
}

/** Hand-drawn 24px stroke icons — no icon library inside a 500 kB bundle ceiling.
 * All aria-hidden: the button's visible label is its accessible name. */
export function Icon({ name }: { name: Destination | 'more' }): JSX.Element {
  const paths: Record<Destination | 'more', JSX.Element> = {
    /* A parcel with an arrow turning back — the return leg, drawn rather than imported: no
       icon library inside a 750 kB bundle ceiling. */
    retour: (
      <>
        <path d="M3.5 8.5 12 4l8.5 4.5v7L12 20l-8.5-4.5v-7Z" />
        <path d="M15 11.5H9m0 0 2.5-2.5M9 11.5 11.5 14" />
      </>
    ),
    boxes: (
      <>
        <path d="M3.5 8.5 12 4l8.5 4.5v7L12 20l-8.5-4.5v-7Z" />
        <path d="M3.5 8.5 12 13l8.5-4.5M12 13v7" />
      </>
    ),
    dashboard: (
      <>
        <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
        <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
        <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
        <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
      </>
    ),
    items: (
      <>
        <path d="M11.6 3.2H4.4a1.2 1.2 0 0 0-1.2 1.2v7.2c0 .32.13.62.35.85l8.4 8.4a1.2 1.2 0 0 0 1.7 0l7.2-7.2a1.2 1.2 0 0 0 0-1.7l-8.4-8.4a1.2 1.2 0 0 0-.85-.35Z" />
        <circle cx="7.5" cy="7.5" r="1.3" />
      </>
    ),
    scan: (
      <>
        <path d="M3.5 8V5.5a2 2 0 0 1 2-2H8" />
        <path d="M16 3.5h2.5a2 2 0 0 1 2 2V8" />
        <path d="M20.5 16v2.5a2 2 0 0 1-2 2H16" />
        <path d="M8 20.5H5.5a2 2 0 0 1-2-2V16" />
        <path d="M7 12h10" />
      </>
    ),
    reservations: (
      <>
        <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
        <path d="M3.5 10h17M8 3v4M16 3v4" />
      </>
    ),
    checkouts: (
      <>
        <path d="M3.5 12h11" />
        <path d="M11 8.5 14.5 12 11 15.5" />
        <path d="M14.5 4.5h4a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2h-4" />
      </>
    ),
    maintenance: (
      <>
        <path d="M14.2 6.9a4.4 4.4 0 0 1 5.6-5.2l-3 3 .5 2 2 .5 3-3a4.4 4.4 0 0 1-5.2 5.6l-8.2 8.2a2 2 0 0 1-2.9-2.9l8.2-8.2Z" />
      </>
    ),
    /* A shelving unit — three shelves in a frame, "where things stand". Same 24px stroke
       vocabulary as its neighbours; no icon library inside the bundle ceiling. */
    racks: (
      <>
        <rect x="3.5" y="4" width="17" height="16" rx="1.5" />
        <path d="M3.5 9.3h17M3.5 14.7h17" />
      </>
    ),
    // A coat hanger: the one shape in this set that says "a look on a rail" without
    // words, and it is what the atelier's own garments hang from.
    outfits: (
      <>
        <path d="M12 3.2a2 2 0 0 1 2 2c0 1.1-.9 1.6-2 2v1.4" />
        <path d="M12 8.6 3.2 15.3a1.3 1.3 0 0 0 .8 2.3h16a1.3 1.3 0 0 0 .8-2.3L12 8.6Z" />
      </>
    ),
    /* A folder with a tab — a production/project, the thing outfits are organised into. Same
       24px stroke vocabulary as its neighbours; no icon library inside the bundle ceiling. */
    projects: (
      <>
        <path d="M3.5 6.5A1.5 1.5 0 0 1 5 5h3.8a1.5 1.5 0 0 1 1.2.6l1 1.4H19a1.5 1.5 0 0 1 1.5 1.5V18A1.5 1.5 0 0 1 19 19.5H5A1.5 1.5 0 0 1 3.5 18V6.5Z" />
      </>
    ),
    /* Two people: a head and shoulders, plus a second set behind. Same 24px stroke
       vocabulary as its neighbours — no icon library inside a 500 kB ceiling. */
    settings: (
      <>
        <path d="M4 8h10M18 8h2M4 16h2M10 16h10" />
        <circle cx="16" cy="8" r="2" />
        <circle cx="8" cy="16" r="2" />
      </>
    ),
    more: (
      <>
        <circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" />
        <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
        <circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none" />
      </>
    ),
  };
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

/**
 * THE PER-DESTINATION SUBTITLE (area-chip redesign 10b): the neutral count of what is in an
 * area, e.g. "4,174 garments". Composed inline in English the way every other count label in the
 * app is (boxes.tsx "N open · M garments", bookings.tsx "N garments held") — NOT an i18n key: the
 * subtitles are compositional totals, Dutch is on indefinite hold, and threading a plural rule
 * through the dictionary for an ornament would diverge from the pattern the screens already use.
 *
 * `null` means "no subtitle" — either the destination has none (scan, dashboard, settings,
 * projects) or its feature is not migrated (`boxesOpen`/`retourWaiting` come back null). A zero is
 * shown (it is an honest total, unlike a warn badge, whose zero is furniture).
 */
function subtitleFor(destination: Destination, counts: NavCounts | null): string | null {
  if (counts === null) return null;
  const plural = (n: number, one: string, many: string): string => `${n.toLocaleString()} ${n === 1 ? one : many}`;
  switch (destination) {
    case 'items':
      return `${counts.itemsTotal.toLocaleString()} garments`;
    case 'boxes':
      return counts.boxesOpen === null ? null : `${counts.boxesOpen.toLocaleString()} open`;
    case 'outfits':
      return plural(counts.outfits, 'look', 'looks');
    case 'projects':
      return plural(counts.projects, 'project', 'projects');
    case 'reservations':
      return plural(counts.reservationDrafts, 'draft', 'drafts');
    case 'checkouts':
      return `${counts.checkoutsOut.toLocaleString()} out`;
    case 'retour':
      return counts.retourWaiting === null ? null : `${plural(counts.retourWaiting, 'parcel', 'parcels')} waiting`;
    case 'racks':
      return plural(counts.racks, 'rack', 'racks');
    default:
      return null;
  }
}

/**
 * The desk sidebar's right-aligned TOTAL per row (the canvas frames, fidelity round 3,
 * 1 Sep 2026) — the bare number, where the phone sheet's subtitle says the phrase. Retour
 * and Maintenance deliberately return null: their number is the WARN badge (already late /
 * waiting on a person), and the frame shows exactly that badge, not a second total.
 */
function totalFor(destination: Destination, counts: NavCounts | null): number | null {
  if (counts === null) return null;
  switch (destination) {
    case 'items':
      return counts.itemsTotal;
    case 'boxes':
      return counts.boxesOpen;
    case 'outfits':
      return counts.outfits;
    case 'projects':
      return counts.projects;
    case 'reservations':
      return counts.reservationDrafts;
    case 'checkouts':
      return counts.checkoutsOut;
    case 'racks':
      return counts.racks;
    default:
      return null;
  }
}

export function AppNav({ route, role }: { route: Route; role: string | null }): JSX.Element {
  useLanguage();
  const open = useNavSheet();
  const active = activeTab(route);
  const visible = visibleDestinations(role);
  const primary = PRIMARY.filter((d) => visible.includes(d));
  const secondary = SECONDARY.filter((d) => visible.includes(d));
  const navRef = useRef<HTMLElement>(null);

  // The warn badges (overdue retour, open maintenance) AND every row's neutral subtitle come
  // from ONE shared read (nav-counts-store) — the same numbers the header band's AreaChip uses,
  // fetched once for both. See that module for the fetched-once/degrade-on-failure reasoning.
  const counts = useNavCounts();
  /**
   * ⚠️ RE-READ ON EVERY SCREEN CHANGE AND WHEN THE TAB COMES BACK (0.1.517). The maintainer, 30 Sep
   * 2026: the desk sidebar said 2 projects while the phone said 3 (the truth), and Check-outs stayed
   * at 0 with a reservation out. Cause: the counts were read ONCE per app load, and re-read only
   * when the PHONE's nav sheet opens (the effect below) — the desk sidebar is always open, so it
   * never re-read and showed whatever was true at page load. Keyed on the path so a navigation is
   * one background read (concurrent reads collapse in the store); fire-and-forget, a failure keeps
   * the previous numbers and is already ledgered.
   */
  const path = routePath(route);
  useEffect(() => {
    void refreshNavCounts();
  }, [path]);
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void refreshNavCounts();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const badgeFor = (destination: Destination): number =>
    counts === null
      ? 0
      : destination === 'retour'
        ? counts.retourOverdue
        : destination === 'maintenance'
          ? counts.maintenanceOpen
          : 0;

  /**
   * FOCUS, SCROLL LOCK AND ESCAPE while the sheet is open (phone only — on a desk the nav is a
   * static sidebar and `open` stays false, the chip being inert there). Focus moves into the
   * sheet (the current area's row, else the first), the body scroll is locked so the list scrolls
   * alone, and Escape closes it. Returning focus to the chip is the chip's own job — it knows its
   * ref (see area-chip.tsx). A minimal first/last trap keeps Tab inside the open sheet; no library,
   * per the bundle ceiling.
   */
  useEffect(() => {
    if (!open) return;
    // THE NUMBERS ARE RE-READ EVERY TIME THE SHEET OPENS (0.1.395) — see nav-counts-store for why
    // the fetch-once cache lied ("1 open" over a tab showing two, surviving an app restart). The
    // read is fire-and-forget: a failure keeps the previous subtitles and is already ledgered.
    void refreshNavCounts();
    // ⚠️ THE LOCK IS THE FIXED-BODY KIND, NOT `overflow: hidden` (0.1.368) — the reasoning now
    // lives with the lock itself in platform/scroll-lock.ts, which every sheet shares since
    // 0.1.395. `nav-sheet-lock` rides along because the band needs pinning while the body is
    // fixed (`body.nav-sheet-lock .area-band` in styles.css); the freeze/restore is the helper's.
    const release = lockBodyScroll('nav-sheet-lock');
    const nav = navRef.current;
    const rows = nav ? Array.from(nav.querySelectorAll<HTMLElement>('button.nav-item')) : [];
    (rows.find((r) => r.getAttribute('aria-current') === 'page') ?? rows[0])?.focus();
    return release;
  }, [open]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (!open) return;
    if (e.key === 'Escape') {
      setNavSheetOpen(false);
      return;
    }
    if (e.key !== 'Tab') return;
    const nav = navRef.current;
    if (nav === null) return;
    const focusable = Array.from(nav.querySelectorAll<HTMLElement>('button'));
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const item = (destination: Destination): JSX.Element => {
    const count = badgeFor(destination);
    const sub = subtitleFor(destination, counts);
    const isActive = destination === active;
    return (
      <button
        key={destination}
        type="button"
        className={isActive ? 'nav-item nav-item-active' : 'nav-item'}
        aria-current={isActive ? 'page' : undefined}
        onClick={() => {
          setNavSheetOpen(false);
          navigate(destination === 'items' ? { name: 'items' } : { name: destination });
        }}
      >
        <Icon name={destination} />
        <span className="nav-item-text">
          <span className="nav-item-label">{t(LABEL[destination])}</span>
          {/* Phone-sheet only: the desk sidebar hides subtitles (CSS), keeping its rows the
              height the maintainer's layout expects. */}
          {sub !== null && <span className="nav-item-sub">{sub}</span>}
        </span>
        {/* Desk-only (CSS): the frame's right-aligned total. One DOM for both layouts —
            the phone sheet hides this and shows the subtitle phrase instead. */}
        {totalFor(destination, counts) !== null && (
          <span className="nav-item-num">{(totalFor(destination, counts) ?? 0).toLocaleString()}</span>
        )}
        {/* Warn tint = "already late / waiting on a person", never a mere total. Zero renders
            nothing: a permanent 0 is furniture that teaches the eye to skip the row. */}
        {count > 0 && <span className="nav-count">{count}</span>}
        {/* The current-area tick (frame 10b). aria-hidden — `aria-current="page"` already carries
            it to assistive tech, and keeping ✓ out of the accessible name is what lets the e2e
            heading/button matchers stay exact. */}
        {isActive && (
          <span className="nav-item-check" aria-hidden="true">
            ✓
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      {/* THE SCRIM (phone only) dims the content BELOW the header band while the menu is open, so
          the band with its chip stays lit at the top (frame 10b). A sibling of the <nav>, not a
          child: inside the nav's stacking context it would paint OVER the destination buttons and
          eat their taps. `open` is only ever true on a phone (the desk chip is inert). Tapping it
          closes, as do Escape, the chip, and picking a destination. */}
      {open && (
        <button
          type="button"
          className="nav-scrim"
          aria-label={t('nav.closeMenu')}
          onClick={() => setNavSheetOpen(false)}
        />
      )}
      <nav
        id="app-nav-sheet"
        className={open ? 'app-nav app-nav-sheet-open' : 'app-nav'}
        aria-label="Main"
        ref={navRef}
        onKeyDown={onKeyDown}
      >
        {/* THE WORDMARK (redesign decision 2): the sidebar leads with MONONOKE. Desktop only —
            CSS hides it on the phone sheet. */}
        <span className="nav-brand" aria-hidden="true">
          MONONOKE
        </span>
      {/**
       * ⚠️ ONE DOM, TWO GROUPINGS, AND THE HEADINGS ARE PLACED SO BOTH COME OUT RIGHT.
       *
       * Phone (10b) and desk both show the SAME flat list in two labelled groups — THE GOODS =
       * the five primaries plus Retour, THE DESK = the rest. Every destination exists exactly
       * once (Playwright strict mode counts hidden nodes; ~20 e2e sites and nav-visibility.test
       * depend on it), so the grouping cannot be a second render of the same buttons.
       *
       * The trick is ORDER: "THE GOODS" heads `nav-primary`, and "THE DESK" sits inside
       * `nav-secondary` AFTER Retour. Both wrappers are `display:contents` in both layouts, so
       * the flattened column reads: GOODS · boxes…check-outs · retour · DESK · projects…settings.
       * The difference between phone and desk is only where the column is painted (a dropped
       * overlay vs a static sidebar) and whether subtitles show — never the DOM.
       */}
      <div className="nav-primary">
        <span className="nav-group-h" aria-hidden="true">
          {t('nav.groupGoods')}
        </span>
        {primary.map(item)}
      </div>
      <div className="nav-secondary">
        {secondary.filter((d) => d === 'retour').map(item)}
        <span className="nav-group-h" aria-hidden="true">
          {t('nav.groupDesk')}
        </span>
        {secondary.filter((d) => d !== 'retour').map(item)}
      </div>
      </nav>
    </>
  );
}
